import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CharacterDraftMeta,
  ConnectedAnswers,
  WizardStepId,
  WorldDraft,
  WorldSnapshot,
  CharacterDraft,
} from '../../domain/types.js';
import type { CharacterBodySnapshot } from './characterBody.js';
import {
  applyReviewBlankFill,
  confirmAllAnswers,
  createFreshDraft,
  ensureScheduleForDraft,
  setConnected,
  setLocationGrounding,
  setReviewAccepted,
  setSchedule,
  setTimePeriod,
  setWizardStep,
  skipConnectedRhythm,
} from './draftReducer.js';
import {
  clearCharacterBodies,
  clearDraftFromStorage,
  loadCharacterBodies,
  loadDraftFromStorage,
  saveCharacterBodies,
  saveDraftToStorage,
} from './draftStorage.js';
import { withSavedCharacterFields } from './character/draftOps.js';
import StepShell from './StepShell.js';
import { bindLocationTimezoneCallbacks } from './wizardLocationTimezone.js';
import {
  canAdvanceFromStep,
  continueLabel,
  firstIncompleteFieldForStep,
  hasResetProgress,
  hasSaveWorldProgress,
  nextStep,
  normalizeWizardStep,
  normalizedLocationGroundingForAdvance,
  normalizedTimePeriodForAdvance,
  prevStep,
  resetToastMessage,
  resolveResumeStep,
  stepTitle,
  visitStepForSave,
  type WizardRouteStep,
} from './wizardFlow.js';
import { acceptReviewAnswerDraft, regenerateFieldAssumption } from './wizardFieldRegen.js';
import { buildLaunchPayload, pause, persistLaunch } from './wizardLaunch.js';
import type { CharacterPhase } from './CharacterPopulationAdapter.js';
import { CharacterStepActions } from './characterStepActions.js';
import {
  CharacterPopulationStep,
  ConnectedStep,
  GenerationStep,
  LocationGroundingStep,
  OperatingHoursStep,
  OperatingHoursStepInit,
  ReviewStep,
  reviewStepContinueBlock,
} from './wizardSteps.js';
import { TimePeriodStep, TimePeriodStepInit } from './periodStep.js';
import {
  fillOptionalBlanksOnStep,
  optionalBlanksForStep,
  persistDefaultGroundingLanguage,
  type OptionalBuilderStep,
} from './optionalBlanks.js';
import { OptionalContinueDialog } from './optionalQuestions.js';
import { getWorldLibrary } from '../library.js';
import { loadPersistedKey } from '../secureStore.js';
import { isHostedSite } from '../../llm/hosted.js';
import type { Npc } from '../../types.js';
import WelcomeIntroStep from './welcomeIntroStep.js';
import { rollVisits } from '../../runtime/visitPresence.js';

function resetWizardDraft(
  draftId: string,
  resetClickedOnce: boolean,
): { draft: ReturnType<typeof createFreshDraft>; toast: string } {
  clearCharacterBodies(draftId);
  clearDraftFromStorage();
  return { draft: createFreshDraft(), toast: resetToastMessage(resetClickedOnce) };
}

export interface CreateWorldLaunchPayload {
  snapshot: WorldSnapshot;
  characters: CharacterDraftMeta[];
  roster: Npc[];
}

export default function CreateWorldWizard(props: {
  initialDraft?: WorldDraft | null;
  initialBodies?: Record<string, CharacterBodySnapshot> | null;
  onExit: () => void;
  onOpenSetup: () => void;
  onKeySaved?: () => void;
  onLaunch: (payload: CreateWorldLaunchPayload) => void;
  /** Set when Add character left a live location. Save returns there. */
  onSavedToLocation?: (draft: WorldDraft, bodies: Record<string, CharacterBodySnapshot>) => void;
}): JSX.Element {
  const hosted = isHostedSite();
  const [skipWelcome, setSkipWelcome] = useState<boolean | null>(hosted ? true : null);
  const [draft, setDraft] = useState<WorldDraft>(() => {
    const loaded = props.initialDraft ?? loadDraftFromStorage() ?? createFreshDraft();
    return { ...loaded, wizardStep: resolveResumeStep(loaded, hosted) };
  });
  const locationTz = bindLocationTimezoneCallbacks(setDraft);
  const [characterBodies, setCharacterBodies] = useState<Record<string, CharacterBodySnapshot>>(() => {
    if (props.initialBodies) return props.initialBodies;
    return loadCharacterBodies((props.initialDraft ?? loadDraftFromStorage() ?? createFreshDraft()).draftId);
  });
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [genStage, setGenStage] = useState('');
  const [genError, setGenError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [editFocusQuestionId, setEditFocusQuestionId] = useState<string | null>(null);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);
  const [draftRevision, setDraftRevision] = useState(1);
  const [resetClickedOnce, setResetClickedOnce] = useState(false);
  const [characterPhase, setCharacterPhase] = useState<CharacterPhase>('basic');
  const characterContinueRef = useRef<(() => void) | null>(null);
  const characterBackRef = useRef<(() => void) | null>(null);
  const [characterContinueDisabled, setCharacterContinueDisabled] = useState<boolean | null>(null);
  const registerCharacterContinue = useCallback((run: (() => void) | null, disabled: boolean) => {
    characterContinueRef.current = run;
    setCharacterContinueDisabled((prev) => (run ? (prev === disabled ? prev : disabled) : null));
  }, []);
  const registerCharacterBack = useCallback((run: (() => void) | null) => {
    characterBackRef.current = run;
  }, []);
  const [optionalDialogOpen, setOptionalDialogOpen] = useState(false);
  const [optionalOpenByStep, setOptionalOpenByStep] = useState<Partial<Record<OptionalBuilderStep, boolean>>>({});
  const [languageExplicitlyCleared, setLanguageExplicitlyCleared] = useState(false);
  const [returnToReview, setReturnToReview] = useState(false);
  const [startEditId, setStartEditId] = useState<string | null>(null);
  const captureWorking = useRef<(() => CharacterDraft | null) | null>(null);
  const draftRef = useRef(draft);
  const bodiesRef = useRef(characterBodies);
  draftRef.current = draft;
  bodiesRef.current = characterBodies;

  useEffect(() => {
    saveDraftToStorage(draft);
    saveCharacterBodies(draft.draftId, characterBodies);
  }, [draft, characterBodies]);

  useEffect(() => {
    let cancelled = false;
    void loadPersistedKey('deepseek').then((key) => {
      if (cancelled) return;
      const hasKey = isHostedSite() || key.trim().length > 0;
      setSkipWelcome(hasKey);
      setDraft((d) => {
        const resumed = resolveResumeStep(d, hasKey);
        if (resumed === d.wizardStep) return d;
        return setWizardStep(d, resumed);
      });
    });
    return () => { cancelled = true; };
  }, []);

  const welcomeSkipped = skipWelcome === true;
  const step = normalizeWizardStep(draft.wizardStep as WizardRouteStep);
  const activeCharacterCount = draft.characterDrafts.filter((c) => c.status !== 'removed').length;

  useEffect(() => {
    if (step === 'review') setReturnToReview(false);
  }, [step]);
  const go = useCallback((s: WizardRouteStep) => {
    setDraft((d) => setWizardStep(d, normalizeWizardStep(s) as WizardStepId));
    setError(null);
  }, []);

  function openConnectedEdit(questionId: string): void {
    setEditFocusQuestionId(questionId);
    setFocusFieldId(questionId);
    go('connected');
  }

  function goToCharacterPopulationFromReview(editId: string | null): void {
    setDraft((d) => setReviewAccepted(d, false));
    setReturnToReview(true);
    setStartEditId(editId);
    go('character-population');
  }

  function backFromCharacterPopulation(): void {
    if (characterBackRef.current) {
      characterBackRef.current();
      return;
    }
    if (props.onSavedToLocation) {
      props.onSavedToLocation(draftRef.current, bodiesRef.current);
      return;
    }
    go(returnToReview ? 'review' : prevStep('character-population', welcomeSkipped));
  }

  function applyFieldRegen(fieldPath: string): void {
    const result = regenerateFieldAssumption(draft, fieldPath);
    if (!result) return;
    setDraft(result.draft);
    setStatus(result.status);
    go(result.step);
  }

  function saveWorldToast(): void {
    const saved = withSavedCharacterFields(draft, bodiesRef.current, captureWorking.current?.() ?? null);
    const draftWithVisit = {
      ...saved.draft,
      lastVisitedStep: visitStepForSave(saved.draft, step),
    };
    saveCharacterBodies(draftWithVisit.draftId, saved.bodies);
    const result = getWorldLibrary().saveDraft({ draft: draftWithVisit, revision: draftRevision });
    setCharacterBodies(saved.bodies);
    setDraftRevision(result.record.revision);
    setDraft(() => ({ ...draftWithVisit, lastVisitedStep: draftWithVisit.lastVisitedStep }));
    setStatus(result.toastMessage);
  }

  function advanceFromStep(working: WorldDraft): void {
    let nextDraft = working;
    if (step === 'location-grounding') {
      nextDraft = persistDefaultGroundingLanguage(nextDraft);
      if (nextDraft.location) {
        nextDraft = normalizedLocationGroundingForAdvance(nextDraft);
      }
      setDraft(nextDraft);
    }

    setFocusFieldId(null);
    setEditFocusQuestionId(null);
    setError(null);

    if (step === 'review') setDraft((d) => confirmAllAnswers(d));

    if (nextStep(step, welcomeSkipped) === 'world-generation') { void runGeneration(); return; }
    go(nextStep(step, welcomeSkipped));
  }

  function tryContinue(): void {
    let working = draft;
    if (step === 'operating-hours' && !working.schedule) {
      working = ensureScheduleForDraft(working);
      setDraft(working);
    }
    if (step === 'time-period' && working.timePeriod) {
      working = normalizedTimePeriodForAdvance(working);
      setDraft(working);
    }

    const block = step === 'review'
      ? reviewStepContinueBlock(working, working.reviewAccepted ?? false)
      : canAdvanceFromStep(working, step);
    if (block) {
      setError(block);
      if (step !== 'review') {
        setFocusFieldId(editFocusQuestionId ?? firstIncompleteFieldForStep(working, step));
      }
      return;
    }

    const optionalStep = step as OptionalBuilderStep;
    if (['location-grounding', 'connected', 'time-period'].includes(optionalStep)) {
      const blanks = optionalBlanksForStep(working, optionalStep, { languageExplicitlyCleared });
      if (blanks.length > 0) {
        setOptionalDialogOpen(true);
        return;
      }
    }

    advanceFromStep(working);
  }

  function handleOptionalYes(): void {
    const optionalStep = step as OptionalBuilderStep;
    let working = fillOptionalBlanksOnStep(draft, optionalStep);
    if (optionalStep === 'location-grounding') {
      working = persistDefaultGroundingLanguage(working);
      working = normalizedLocationGroundingForAdvance(working);
    }
    if (optionalStep === 'time-period' && working.timePeriod) {
      working = normalizedTimePeriodForAdvance(working);
    }
    setDraft(working);
    setOptionalDialogOpen(false);
    advanceFromStep(working);
  }

  function handleOptionalNo(): void {
    const optionalStep = step as OptionalBuilderStep;
    setOptionalOpenByStep((prev) => ({ ...prev, [optionalStep]: true }));
    setOptionalDialogOpen(false);
    const blanks = optionalBlanksForStep(draft, optionalStep, { languageExplicitlyCleared });
    setFocusFieldId(blanks[0]?.fieldId ?? null);
  }

  async function runGeneration(): Promise<void> {
    setLoading(true); setGenError(null); setGenStage('Validating world…'); go('world-generation');
    await pause(80); setGenStage('Generating plot devices (local)…'); await pause(80);
    setGenStage('Building snapshot…');
    const built = await buildLaunchPayload(draftRef.current, bodiesRef.current);
    setLoading(false);
    if (!built.ok) { setGenError(built.error); return; }
    setGenStage('Ready to launch'); go('ready-to-launch');
    persistLaunch(built.payload, draftRef.current.draftId);
    props.onLaunch(built.payload);
  }

  function handleGroundingAiFill(next: WorldDraft, summary: string): void {
    setDraft(next);
    setStatus(summary);
  }

  function handleConnectedChange(
    connected: ConnectedAnswers,
    options?: { syncOptional?: boolean },
  ): void {
    setDraft((d) => setConnected(d, connected, { syncOptional: options?.syncOptional }));
  }

  function handleConnectedAiFill(next: WorldDraft, summary: string): void {
    setDraft(next);
    setStatus(summary);
  }

  function handlePeriodChange(period: import('../../domain/types.js').TimePeriod): void {
    setDraft((d) => setTimePeriod(d, period));
  }

  function renderStep(): JSX.Element {
    const activeFocus = editFocusQuestionId ?? focusFieldId;

    switch (step) {
      case 'welcome':
        return (
          <WelcomeIntroStep
            onOpenSetup={props.onOpenSetup}
            onKeySaved={() => props.onKeySaved?.()}
          />
        );
      case 'location-grounding':
        return (
          <LocationGroundingStep
            draft={draft}
            location={draft.location}
            focusFieldId={activeFocus}
            optionalOpen={optionalOpenByStep['location-grounding']}
            onOptionalOpenChange={(open) => setOptionalOpenByStep((p) => ({ ...p, 'location-grounding': open }))}
            languageExplicitlyCleared={languageExplicitlyCleared}
            onLanguageExplicitlyClearedChange={setLanguageExplicitlyCleared}
            onChange={(loc) => setDraft((d) => setLocationGrounding(d, loc))}
            {...locationTz}
            onAiFill={handleGroundingAiFill}
          />
        );
      case 'connected':
        return (
          <ConnectedStep
            draft={draft}
            connected={draft.connected}
            focusFieldId={activeFocus}
            optionalOpen={optionalOpenByStep.connected}
            onOptionalOpenChange={(open) => setOptionalOpenByStep((p) => ({ ...p, connected: open }))}
            onChange={handleConnectedChange}
            onSkipRhythm={() => setDraft((d) => skipConnectedRhythm(d))}
            onAiFill={handleConnectedAiFill}
          />
        );
      case 'time-period':
        return (
          <TimePeriodStepInit
            period={draft.timePeriod}
            onInit={(p) => setDraft((d) => setTimePeriod(d, p))}
          >
            {(period) => (
              <TimePeriodStep
                period={period}
                draftId={draft.draftId}
                focusFieldId={activeFocus}
                optionalOpen={optionalOpenByStep['time-period']}
                onOptionalOpenChange={(open) => setOptionalOpenByStep((p) => ({ ...p, 'time-period': open }))}
                onChange={handlePeriodChange}
              />
            )}
          </TimePeriodStepInit>
        );
      case 'operating-hours':
        return (
          <OperatingHoursStepInit draft={draft} onInit={(s) => setDraft((d) => setSchedule(d, s))}>
            {(schedule) => (
              <OperatingHoursStep
                schedule={schedule}
                locationRegion={draft.location?.region}
                draftId={draft.draftId}
                focusFieldId={activeFocus}
                onChange={(s) => setDraft((d) => setSchedule(d, s))}
                {...locationTz}
              />
            )}
          </OperatingHoursStepInit>
        );
      case 'review':
        return (
          <ReviewStep
            draft={draft}
            confirmed={draft.reviewAccepted ?? false}
            onConfirm={() => setDraft((d) => setReviewAccepted(d, !(d.reviewAccepted ?? false)))}
            onEditSection={(s) => go(normalizeWizardStep(s as WizardRouteStep))}
            onEditAnswer={openConnectedEdit}
            onAcceptAnswer={(qid) => setDraft((d) => acceptReviewAnswerDraft(d, qid))}
            onRegenerateAnswer={(qid) => applyFieldRegen(`answers.${qid}`)}
            onRegenerateSection={(field) => applyFieldRegen(field)}
            onEditCharacter={(id) => goToCharacterPopulationFromReview(id)}
            onAddCharacter={() => goToCharacterPopulationFromReview(null)}
            onFillBlanks={() => setDraft((d) => applyReviewBlankFill(d))}
            onDeclineFillBlanks={() => {}}
            bodies={characterBodies}
            onBondsChange={(bonds) => setDraft((d) => ({ ...d, characterBonds: bonds }))}
            onVisitsChange={(characterId, visits, visitor) => setDraft((d) => ({ ...d, characterDrafts: d.characterDrafts.map((character) => character.draftCharacterId === characterId ? { ...character, visits: rollVisits(visits), visitor: visitor ?? character.visitor } : character) }))}
          />
        );
      case 'character-population':
        return (
          <CharacterPopulationStep
            draft={draft}
            bodies={characterBodies}
            onDraftChange={setDraft}
            onBodiesChange={setCharacterBodies}
            onBack={backFromCharacterPopulation}
            onSaveToast={saveWorldToast}
            onPhaseChange={setCharacterPhase}
            shellOwnsContinue
            onRegisterContinue={registerCharacterContinue}
            onRegisterBack={registerCharacterBack}
            captureWorking={captureWorking}
            startEditId={startEditId}
            onStartEditConsumed={() => setStartEditId(null)}
            onSavedToLocation={props.onSavedToLocation}
            onCharacterFlowComplete={() => {
              setFocusFieldId(null);
              setEditFocusQuestionId(null);
              setError(null);
              go(nextStep(step, welcomeSkipped));
            }}
            onReset={() => {
              if (!window.confirm('Reset the entire draft?')) return;
              const { draft: fresh, toast } = resetWizardDraft(draft.draftId, resetClickedOnce);
              setDraft(fresh);
              setCharacterBodies({});
              setEditFocusQuestionId(null);
              setFocusFieldId(null);
              setDraftRevision(1);
              setStatus(toast);
              setResetClickedOnce(true);
            }}
          />
        );
      case 'world-generation':
      case 'ready-to-launch':
        return <GenerationStep stage={genStage} error={genError} onRetry={() => void runGeneration()} />;
      default:
        return (
          <WelcomeIntroStep
            onOpenSetup={props.onOpenSetup}
            onKeySaved={() => props.onKeySaved?.()}
          />
        );
    }
  }

  return (
    <StepShell
      step={step}
      title={stepTitle(step)}
      error={error}
      status={status}
      loading={loading}
      showBack={step !== 'welcome'}
      showSaveWorld={step !== 'welcome' && hasSaveWorldProgress(draft)}
      showReset={step !== 'welcome' && hasResetProgress(draft)}
      onBack={step === 'welcome' ? undefined : () => {
        if (step === 'character-population') backFromCharacterPopulation();
        else go(prevStep(step, welcomeSkipped));
      }}
      onSaveWorld={saveWorldToast}
      onReset={() => {
        if (!window.confirm('Reset the entire draft?')) return;
        const { draft: fresh, toast } = resetWizardDraft(draft.draftId, resetClickedOnce);
        setDraft(fresh);
        setCharacterBodies({});
        setEditFocusQuestionId(null);
        setFocusFieldId(null);
        setDraftRevision(1);
        setStatus(toast);
        setResetClickedOnce(true);
      }}
      actions={step === 'ready-to-launch' ? null : step === 'character-population' ? (
        <CharacterStepActions
          phase={characterPhase}
          continueDisabled={characterContinueDisabled}
          loading={loading}
          activeCount={activeCharacterCount}
          continueLabel={continueLabel(step, welcomeSkipped)}
          onContinueForm={() => characterContinueRef.current?.()}
          onContinueStep={tryContinue}
          onSaveEdits={props.onSavedToLocation
            ? () => props.onSavedToLocation?.(draftRef.current, bodiesRef.current)
            : null}
        />
      ) : (
        <button type="button" className="btn-primary" onClick={tryContinue} disabled={loading}>
          {step === 'world-generation' ? 'Retry' : continueLabel(step, welcomeSkipped)}
        </button>
      )}>
      {renderStep()}
      <OptionalContinueDialog
        open={optionalDialogOpen}
        onYes={handleOptionalYes}
        onNo={handleOptionalNo}
      />
    </StepShell>
  );
}
